/**
 * Contexto de geração do Pro (módulo puro — sem IA, sem Maps, sem créditos).
 *
 * Cada pedido novo, mudança material do texto ou alteração do destino tem de
 * criar um contexto NOVO. Nenhum dado derivado da geração anterior
 * (experiência curada, requisitos, reparações, cidades, erros) pode
 * sobreviver a essa mudança.
 *
 * Regra genérica: nada aqui conhece países, cidades ou experiências
 * concretas — só compara a assinatura do formulário atual.
 */

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, " ")
    .trim();

export type GenerationContextInput = {
  prompt: string;
  cities: string;
  days?: number;
  transport?: string;
  accommodation?: string;
  startDate?: string;
  endDate?: string;
};

/** Assinatura estável do pedido atual. */
export function generationContextKey(input: GenerationContextInput): string {
  return [
    fold(input.prompt),
    fold(input.cities),
    String(input.days ?? ""),
    fold(input.transport ?? ""),
    fold(input.accommodation ?? ""),
    input.startDate ?? "",
    input.endDate ?? "",
  ].join("|");
}

/** Semelhança grosseira entre dois textos (Jaccard de palavras). */
function textSimilarity(a: string, b: string): number {
  const wa = new Set(fold(a).split(" ").filter(Boolean));
  const wb = new Set(fold(b).split(" ").filter(Boolean));
  if (!wa.size && !wb.size) return 1;
  if (!wa.size || !wb.size) return 0;
  let shared = 0;
  for (const w of wa) if (wb.has(w)) shared += 1;
  return shared / (wa.size + wb.size - shared);
}

/**
 * Mudança material: destino diferente, dias diferentes, transporte diferente
 * ou texto suficientemente distinto (< 70% de palavras em comum).
 */
export function isMaterialChange(
  previous: GenerationContextInput | null | undefined,
  next: GenerationContextInput,
): boolean {
  if (!previous) return true;
  if (generationContextKey(previous) === generationContextKey(next)) return false;
  if (fold(previous.cities) !== fold(next.cities)) return true;
  if ((previous.days ?? 0) !== (next.days ?? 0)) return true;
  if (fold(previous.transport ?? "") !== fold(next.transport ?? "")) return true;
  return textSimilarity(previous.prompt, next.prompt) < 0.7;
}

/** Estado derivado de uma geração — tudo o que tem de ser limpo. */
export type DerivedGenerationState<TPlan = unknown, TTemplate = unknown, TZone = unknown> = {
  plan: TPlan | null;
  selectedExperience: TTemplate | null;
  experienceOptions: TTemplate[] | null;
  templateMode: "full" | "partial" | null;
  otherExperience: string;
  repairOfId: string | null;
  repairStrategy: string | null;
  repairCandidates: unknown[];
  violations: unknown[];
  compliance: unknown | null;
  requirements: unknown | null;
  handoffApplied: boolean;
  cities: string[];
  zones: TZone[];
  shareSlug: string | null;
  isSaved: boolean;
  error: string | null;
};

/** Contexto limpo — o único ponto de verdade do que "zerar" significa. */
export function emptyGenerationState<
  TPlan = unknown,
  TTemplate = unknown,
  TZone = unknown,
>(): DerivedGenerationState<TPlan, TTemplate, TZone> {
  return {
    plan: null,
    selectedExperience: null,
    experienceOptions: null,
    templateMode: null,
    otherExperience: "",
    repairOfId: null,
    repairStrategy: null,
    repairCandidates: [],
    violations: [],
    compliance: null,
    requirements: null,
    handoffApplied: false,
    cities: [],
    zones: [],
    shareSlug: null,
    isSaved: false,
    error: null,
  };
}

/**
 * Devolve o estado a usar na próxima geração: limpo quando houve mudança
 * material, o anterior quando é a mesma pesquisa (ex.: reparação autorizada).
 */
export function nextGenerationState<TPlan, TTemplate, TZone>(input: {
  previousInput?: GenerationContextInput | null;
  nextInput: GenerationContextInput;
  previousState: DerivedGenerationState<TPlan, TTemplate, TZone>;
  isRepair?: boolean;
}): { reset: boolean; state: DerivedGenerationState<TPlan, TTemplate, TZone> } {
  if (input.isRepair) return { reset: false, state: input.previousState };
  if (!isMaterialChange(input.previousInput, input.nextInput)) {
    return { reset: false, state: input.previousState };
  }
  return { reset: true, state: emptyGenerationState<TPlan, TTemplate, TZone>() };
}
